import { format as formatDate } from 'date-fns';
import {
  Account,
  AccountType,
  CustomerStorageType,
  Transaction,
  TransactionDates,
  Customer,
  SortDirection,
} from '@algoan/dashboard/model';
import { lowerCaseAndRemoveAccents } from '@algoan/utils/string';

/**
 * A version `String.replaceAll` compatible with old browsers.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replaceAll
 * @param string the string to handle
 * @param substr the sub string to replace
 * @param newSubstr the sub string that replace the old sub string
 */
export const replaceAll = (string: string, substr: string, newSubstr: string): string =>
  string.replace(new RegExp(substr, 'g'), newSubstr);

/**
 * Build a complete URL from a base url and query param data
 * @param baseUrl
 * @param params
 */
export const buildUrl = (
  baseUrl: string,
  params: Array<{ key: string; value: string | number | Record<string, unknown> | undefined }>,
): string => {
  const queryParams = params.reduce<string[]>((acc, param) => {
    const { key, value } = param;

    if (!value) {
      return acc;
    } else {
      const newQueryParam = key + '=' + encodeURIComponent(typeof value === 'object' ? JSON.stringify(value) : value);
      acc.push(newQueryParam);

      return acc;
    }
  }, []);

  return baseUrl + (queryParams.length === 0 ? '' : '?' + queryParams.join('&'));
};

/**
 * Formats the given date to be used in file names in the application
 * @param date
 * @returns
 */
export const formatDateForFile = (date: Date): string => formatDate(date, 'yyyy-MM-dd');

/**
 * Convert a SortDirection to a valid mongo sort value
 * @param sortDirection
 */
export const getMongoSortValue = (sortDirection?: SortDirection): -1 | 1 => (sortDirection === 'ASC' ? 1 : -1);

export const haveSameSign = (x1: number, x2: number): boolean => (x1 >= 0 && x2 >= 0) || (x1 <= 0 && x2 <= 0);

/**
 * Helper function to choose between debitedAt or bookedAt as the date to be used.
 */
export const selectDate = (transactionDate: TransactionDates): string => {
  if (transactionDate?.bookedAt) return transactionDate.bookedAt;
  else if (transactionDate?.debitedAt) return transactionDate.debitedAt;
  else return '';
};

export const allowOnlyNumbersInInput = (
  language: string,
  inputValue: string,
  setValueInput: (newValue: string) => void,
): void => {
  // Regex : number only (0 number or more) 🀰 `.` or `,` separator optional 🀰 number only (0 to 2 numbers) optional
  const regexNumberOnly = /^[0-9]*([,\.]\d{0,2})?$/;
  if (regexNumberOnly.test(inputValue) || inputValue.length === 0) {
    setValueInput(language === 'fr' ? inputValue.replace('.', ',') : inputValue.replace(',', '.'));
  }
};

export const convertStringToNumber = (string: string): number =>
  isNaN(parseFloat(string.replace(',', '.'))) ? 0 : parseFloat(string.replace(',', '.'));

const getOwnerNameOfFirstAggregatedAccount = (accounts: Account[]): string | undefined => {
  if (accounts && accounts.length > 0) {
    for (const account of accounts) {
      if (account.type === AccountType.CHECKING && account?.owners?.[0]?.name) {
        return account.owners[0].name;
      }
    }
  }
  return undefined;
};

export const getFullName = (customer: Customer, accounts: Account[]): string | undefined => {
  const firstName = customer.personalDetails?.identity?.firstName;
  const lastName = customer.personalDetails?.identity?.lastName;

  if (!firstName && !lastName) {
    return getOwnerNameOfFirstAggregatedAccount(accounts);
  }

  return (firstName ?? '') + ' ' + (lastName ?? '');
};

/**
 * returns a string where all regex special chars are escaped
 */
export const escapeRegexReservedCharacters = (word: string): string => word.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

export const matchWordToSearch = (sentence: string, wordToSearch: string): boolean =>
  lowerCaseAndRemoveAccents(sentence ?? '').includes(lowerCaseAndRemoveAccents(wordToSearch));

export const extractTimestampFromObjectId = (objectId: string): number => {
  return parseInt(objectId.substring(0, 8), 16);
};

export const isAnonymousCustomer = (customer?: Customer): boolean => {
  return (
    customer?.storage?.type === CustomerStorageType.PARTIAL_DELETION &&
    customer.storage.partiallyExpiresAt !== undefined &&
    new Date() > new Date(customer.storage.partiallyExpiresAt)
  );
};

/**
 * @param number - a float number with a random number of decimals
 *     ie: 100.123456
 * @returns the rounded number, ie 100.12
 */
export const roundDecimals = (number: number): number => {
  return Math.round(number * 100) / 100;
};

export const isRejectedTransaction = (transaction: Transaction): boolean => {
  return transaction.enrichments?.relatedTransactions?.rejectedOn !== undefined;
};

export const capitalizeFirstLetter = (string: string): string => {
  return `${string.charAt(0).toUpperCase() + string.slice(1)} `;
};

export const composeFullName = (firstName?: string, lastName?: string): string => {
  return `${capitalizeFirstLetter(firstName?.toLowerCase() ?? '')}${lastName?.toUpperCase()}`;
};

export const sumTransactionsAmount = (transactions: Transaction[]): number =>
  transactions.reduce((sum, transaction) => sum + transaction.amount, 0);

export const convertStringToDate = (date: string | undefined): Date | null => (date ? new Date(date) : null);
